import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import CTAButtons from '../components/CTAButtons';
import RegistrationModal from '../components/RegistrationModal';

const CopyrightRegistration = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const packages = [
    {
      name: 'Standard Package',
      price: 99,
      features: [
        'Complete Copyright Application',
        'Original Work Documentation',
        'Filing with U.S. Copyright Office',
        'Application Status Tracking',
        'Email Support'
      ]
    },
    {
      name: 'Premium Package',
      price: 199,
      featured: true,
      features: [
        'Everything in Standard Package',
        'Priority Processing',
        'Dedicated Copyright Specialist',
        'Phone & Email Support',
        'Copyright Certificate Delivery',
        'Infringement Monitoring Setup'
      ]
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Copyright Registration - Protect Your Creative Work | Global Trademark USA</title>
        <meta name="description" content="Secure copyright protection for your creative works. Professional copyright registration services with expert guidance and fast processing." />
        <meta name="keywords" content="copyright registration, copyright protection, creative works, intellectual property, copyright filing" />
        <link rel="canonical" href="https://yourdomain.com/copyright-registration" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-xl font-semibold">
              Copyright Protection
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8">
              Protect Your Creative Works with 
              <span className="text-blue-600">Copyright Registration</span>
            </h1>
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
              Secure legal protection for your original creative works including books, music, software, and artistic creations. Get started today.
            </p>
            <CTAButtons buttonText="Register My Copyright" />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Copyright Package
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional copyright registration services tailored to your needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {packages.map((pkg, index) => (
              <Card key={index} className={`relative ${pkg.featured ? 'border-blue-300 shadow-xl scale-105' : 'border-gray-200'} h-[473px] flex flex-col`}>
                {pkg.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-1 rounded-full text-xl font-semibold">
                    Most Popular
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                  <div className="text-3xl font-bold text-gray-900">
                    ${pkg.price}
                  </div>
                </CardHeader>
                <CardContent className="flex-1 pb-3">
                  <div className="max-h-[240px] overflow-y-auto pr-2">
                    <ul className="feature-list space-y-0">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx}>
                          <Check className="w-5 h-5 text-blue-600" />
                          <span className="text-base text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={() => setIsModalOpen(true)} 
                    className={`w-full ${
                      pkg.featured 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800' 
                        : 'bg-gray-900 hover:bg-gray-800'
                    }`}
                  >
                    Register My Copyright
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
              Why Choose Our Copyright Services?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Expert Guidance</h3>
                <p className="text-base text-gray-600">
                  Professional copyright specialists guide you through every step of the registration process.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Fast Processing</h3>
                <p className="text-base text-gray-600">
                  Quick turnaround times with priority processing options for urgent copyright needs.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Legal Protection</h3>
                <p className="text-base text-gray-600">
                  Secure comprehensive legal protection for your creative works and intellectual property.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Protect Your Creative Work?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of creators who have secured their copyrights with our expert services.
            </p>
            <CTAButtons buttonText="Start My Copyright Registration" variant="dark" />
          </div>
        </div>
      </section>

      <RegistrationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
};

export default CopyrightRegistration;
